import type { MeEnterprise, OrgMap, OrgMapMember } from '@/api'
import { memberLabel } from '@/lib/memberLabel'

/** Enough of a member to draw them: both team members and team-less ones carry this much. */
type OrgMapMemberLike = Pick<OrgMapMember, 'id' | 'name' | 'displayName' | 'avatarUrl'> & {
    roles?: OrgMapMember['roles']
}

/**
 * Every level of the company, outermost first. A task is the terminal level. The portfolio is above the
 * company: every enterprise the signed-in person holds, drawn only when there is more than one.
 */
export type OrgNodeKind = 'portfolio' | 'enterprise' | 'team' | 'member' | 'role' | 'task'

/**
 * The company as one containment tree, built from `GET /orgs`. Each node knows the page it opens, so the
 * map never has to rebuild a route from a kind and an id twice.
 */
export type OrgTreeNode = {
    id: string
    kind: OrgNodeKind
    name: string
    /** The detail page for the underlying entity, independent of this occurrence's parent. */
    route: string
    avatarUrl?: string | null
    /**
     * How many people this circle stands for — a team's members, an enterprise's whole staff — which is
     * what its size follows. Absent where size says nothing (a member, a role, a task).
     */
    weight?: number
    /**
     * Set on an enterprise the person holds but is not signed into: the id to switch to. Such a circle has
     * no children to enter, since its teams belong to a different session.
     */
    switchTo?: string
    children: OrgTreeNode[]
}

/** Minimum diameter of the centred circle; larger groups grow to keep their children separate. */
export const ORG_CENTER_SIZE = 640
const ORG_CHILD_SIZE = 240
const ORG_CHILD_GAP = 48
const ORG_HEADER_HEIGHT = 80
const ORG_CIRCLE_PADDING = 40
/** A sole child fills more of the middle. */
const ORG_SOLO_CHILD_SIZE = 340
/**
 * A team is the company's main division, and holds a row of member faces: it gets the larger circle, and
 * within this range a bigger team draws bigger, so the map shows at a glance where the people are.
 */
export const ORG_TEAM_SIZE_MIN = 240
export const ORG_TEAM_SIZE_MAX = 360
/** Someone on no team sits beside the teams, plainly smaller than one. */
const ORG_UNTEAMED_MEMBER_SIZE = 190
/** An enterprise among the person's others: the same rule, by how many people it employs. */
export const ORG_ENTERPRISE_SIZE_MIN = 240
export const ORG_ENTERPRISE_SIZE_MAX = 400
/** The portfolio's own id: there is one, and it is not a record with an id of its own. */
export const ORG_PORTFOLIO_ID = 'portfolio:all'

/** One circle on the canvas: where it sits, how big it is, and what is inside it. */
export type OrgGraphNode = {
    id: string
    kind: OrgNodeKind
    name: string
    route: string
    avatarUrl?: string | null
    /** Top-left canvas coordinates, the way Vue Flow positions a node. */
    position: { x: number; y: number }
    /** Width and height: every circle is round. */
    size: number
    /** The circle the canvas is centred on; everything else in the graph is one of its children. */
    center: boolean
    /** Tasks open their page; containers can still be entered when they have no children. */
    leaf: boolean
    /** An enterprise the person could sign into instead: the click offers the switch. */
    switchTo?: string
    weight?: number
    childCount: number
    /**
     * What this circle holds, one entry per child, for the preview under its count: a team shows its
     * members' faces, a member its role names, a role its task names.
     */
    holds: Array<{ id: string; name: string; avatarUrl?: string | null }>
}

/** A containment line from a circle to one of the circles it holds. */
export type OrgGraphEdge = { id: string; source: string; target: string }

export type OrgGraph = {
    center: OrgGraphNode
    /** The centred circle and its direct children — never a grandchild. */
    nodes: OrgGraphNode[]
    edges: OrgGraphEdge[]
    /** Enterprise-first path down to the centred circle, for the trail above the canvas. */
    trail: OrgTreeNode[]
}

export function orgNodeRoute(kind: OrgNodeKind, id: string): string {
    switch (kind) {
        case 'portfolio':
            return '/enterprises'
        case 'enterprise':
            return '/enterprise'
        case 'team':
            return `/teams/${id}`
        case 'member':
            return `/members/${id}`
        case 'role':
            return `/roles/${id}`
        case 'task':
            return `/tasks/${id}`
    }
}

function node(kind: OrgNodeKind, id: string, name: string, parentId?: string): OrgTreeNode {
    const occurrence = `${kind}:${encodeURIComponent(id)}`
    return {
        id: parentId ? `${parentId}/${occurrence}` : occurrence,
        kind,
        name,
        route: orgNodeRoute(kind, id),
        children: [],
    }
}

/**
 * IDs identify occurrences, because one member may sit in several teams and one role may have
 * several holders. Keeping the full parent path prevents drilling into the wrong occurrence.
 *
 * With `enterprises` — everything the signed-in person holds — and more than one of them, the tree gains
 * a portfolio root above the company: the signed-in enterprise, with its teams, sits beside the others,
 * which have no children and switch the session when entered. With one, the company stays the root.
 */
export function buildOrgTree(map: OrgMap, enterprises: MeEnterprise[] = []): OrgTreeNode {
    const enterprise = buildEnterpriseTree(map)
    if (enterprises.length <= 1) {
        return enterprise
    }
    const portfolio: OrgTreeNode = {
        id: ORG_PORTFOLIO_ID,
        kind: 'portfolio',
        name: 'Your enterprises',
        route: orgNodeRoute('portfolio', ''),
        children: [],
    }
    portfolio.children = enterprises.map(held => {
        if (held.id === map.enterprise.id) {
            return {
                ...enterprise,
                id: `${portfolio.id}/${enterprise.id}`,
                children: reparent(enterprise, portfolio.id),
            }
        }
        const other = node('enterprise', held.id, held.name, portfolio.id)
        other.avatarUrl = held.avatarUrl ?? null
        other.weight = held.memberCount
        other.switchTo = held.id
        return other
    })
    return portfolio
}

/** The same subtree, with every occurrence id re-rooted under `parentId`. */
function reparent(parent: OrgTreeNode, parentId: string): OrgTreeNode[] {
    const prefix = `${parentId}/${parent.id}`
    const rebase = (child: OrgTreeNode, from: string, to: string): OrgTreeNode => ({
        ...child,
        id: `${to}${child.id.slice(from.length)}`,
        children: child.children.map(grandchild => rebase(grandchild, from, to)),
    })
    return parent.children.map(child => rebase(child, parent.id, prefix))
}

/** The one company as the map knew it before portfolios: the enterprise, its teams, and everyone in them. */
function buildEnterpriseTree(map: OrgMap): OrgTreeNode {
    const enterprise = node('enterprise', map.enterprise.id, map.enterprise.name)
    enterprise.avatarUrl = map.enterprise.avatarUrl ?? null
    const memberNode = (member: OrgMapMemberLike, parentId: string): OrgTreeNode => {
        // Labelled as everywhere else in the portal (#1815): the unnamed COO is a circle reading "COO", not a blank.
        const built = node('member', member.id, memberLabel(member), parentId)
        built.avatarUrl = member.avatarUrl ?? null
        // A portal can be newer than the API it talks to; an older map sends a member with no roles.
        built.children = (member.roles ?? []).map(role => {
            const roleNode = node('role', role.id, role.name, built.id)
            roleNode.children = role.tasks.map(task => node('task', task.id, task.name, roleNode.id))
            return roleNode
        })
        return built
    }
    const teams = map.teams.map(team => {
        const teamNode = node('team', team.id, team.name, enterprise.id)
        teamNode.children = team.members.map(member => memberNode(member, teamNode.id))
        teamNode.weight = team.members.length
        return teamNode
    })
    // Someone on no team belongs to the company all the same: they sit beside the teams, not off the map.
    const unteamed = (map.unplaced?.members ?? []).map(member => memberNode(member, enterprise.id))
    enterprise.children = [...teams, ...unteamed]
    // The company's headcount: everyone drawn somewhere in it, counted once each.
    enterprise.weight = new Set([
        ...map.teams.flatMap(team => team.members.map(m => m.id)),
        ...unteamed.map(m => m.id),
    ]).size
    return enterprise
}

/** How many levels the tree has: the footer's "Level n of N". */
export function orgDepth(tree: OrgTreeNode): number {
    return 1 + Math.max(0, ...tree.children.map(orgDepth))
}

/** Enterprise-first path down to `id`, or an empty path when the tree does not hold it. */
export function orgTrail(tree: OrgTreeNode, id: string): OrgTreeNode[] {
    if (tree.id === id) {
        return [tree]
    }
    for (const child of tree.children) {
        const found = orgTrail(child, id)
        if (found.length > 0) {
            return [tree, ...found]
        }
    }
    return []
}

export function findOrgNode(tree: OrgTreeNode, id: string): OrgTreeNode | null {
    return orgTrail(tree, id).at(-1) ?? null
}

/**
 * Two levels, never three: the circle the canvas is centred on, and the circles directly inside it. A
 * grandchild is not in the returned node set at all — it arrives when its own parent is centred, which is
 * what keeps a company of any size readable.
 *
 * Children occupy an evenly spaced grid contained by the centred circle. The parent grows instead
 * of squeezing a large group onto an overlapping ring, and its heading has its own clear area.
 */
/**
 * A diameter between `min` and `max` for a circle standing for `weight` people among siblings whose
 * largest stands for `heaviest`. Area, not diameter, follows headcount, so twice the people reads as
 * twice the circle rather than four times. The heaviest sibling fills the range; an empty one sits at
 * the floor.
 */
export function scaleSize(weight: number, heaviest: number, min: number, max: number): number {
    if (heaviest <= 0 || weight <= 0) return min
    return Math.round(min + (max - min) * Math.sqrt(Math.min(weight, heaviest) / heaviest))
}

/**
 * How big one child circle is. Inside the enterprise the two kinds sit side by side and must read as
 * different weights: a team is a division of the company, a team-less member is one person. Teams, and
 * enterprises inside the portfolio, grow with the people in them.
 */
function childSize(child: OrgTreeNode, parent: OrgTreeNode, count: number): number {
    const heaviest = Math.max(0, ...parent.children.map(sibling => sibling.weight ?? 0))
    if (parent.kind === 'portfolio') {
        return scaleSize(child.weight ?? 0, heaviest, ORG_ENTERPRISE_SIZE_MIN, ORG_ENTERPRISE_SIZE_MAX)
    }
    if (parent.kind === 'enterprise') {
        return child.kind === 'team'
            ? scaleSize(child.weight ?? 0, heaviest, ORG_TEAM_SIZE_MIN, ORG_TEAM_SIZE_MAX)
            : ORG_UNTEAMED_MEMBER_SIZE
    }
    return count === 1 ? ORG_SOLO_CHILD_SIZE : ORG_CHILD_SIZE
}

export function buildOrgGraph(tree: OrgTreeNode, centeredId?: string | null): OrgGraph {
    const trail = orgTrail(tree, centeredId ?? tree.id)
    const resolved = trail.length > 0 ? trail : [tree]
    const centered = resolved[resolved.length - 1] ?? tree
    const count = centered.children.length
    const sizes = centered.children.map(child => childSize(child, centered, count))
    // One grid, cells wide enough for the largest circle in it, so mixed sizes still cannot collide.
    const cell = Math.max(ORG_CHILD_SIZE, ...sizes)
    const columns = Math.ceil(Math.sqrt(count))
    const rows = Math.ceil(count / Math.max(columns, 1))
    const step = cell + ORG_CHILD_GAP
    const children: OrgGraphNode[] = centered.children.map((child, index) => {
        const size = sizes[index] ?? ORG_CHILD_SIZE
        const row = Math.floor(index / columns)
        const rowCount = Math.min(columns, count - row * columns)
        const x = ((index % columns) - (rowCount - 1) / 2) * step
        const y = (row - (rows - 1) / 2) * step + 64
        return {
            id: child.id,
            kind: child.kind,
            name: child.name,
            route: child.route,
            avatarUrl: child.avatarUrl,
            position: { x: x - size / 2, y: y - size / 2 },
            size,
            center: false,
            // A switchable enterprise ends the walk too: its inside belongs to another session.
            leaf: child.kind === 'task' || Boolean(child.switchTo),
            switchTo: child.switchTo,
            weight: child.weight,
            childCount: child.children.length,
            holds: child.children.map(held => ({
                id: held.id,
                name: held.name,
                avatarUrl: held.avatarUrl ?? null,
            })),
        }
    })
    const radius = Math.max(
        ORG_CENTER_SIZE / 2,
        ...children.map(child =>
            Math.max(
                Math.hypot(child.position.x + child.size / 2, child.position.y + child.size / 2) +
                    child.size / 2 +
                    ORG_CIRCLE_PADDING,
                ORG_HEADER_HEIGHT - child.position.y,
            ),
        ),
    )
    const centerSize = Math.ceil(radius / 8) * 16
    const center: OrgGraphNode = {
        id: centered.id,
        kind: centered.kind,
        name: centered.name,
        route: centered.route,
        avatarUrl: centered.avatarUrl,
        position: { x: -centerSize / 2, y: -centerSize / 2 },
        size: centerSize,
        center: true,
        leaf: centered.kind === 'task',
        weight: centered.weight,
        childCount: centered.children.length,
        holds: centered.children.map(held => ({ id: held.id, name: held.name, avatarUrl: held.avatarUrl ?? null })),
    }
    return {
        center,
        nodes: [center, ...children],
        edges: children.map(child => ({ id: `${center.id}->${child.id}`, source: center.id, target: child.id })),
        trail: resolved,
    }
}
